// DepensesFixesSettingsPage.jsx
//
// Le MODELE de depense fixe — le loyer, l'electricite, l'abonnement — vit ici,
// avec les autres reglages d'ecole. Il vivait dans le portefeuille, ou il
// detonnait : la table `depenses_fixes_recurrentes` ne porte aucun
// gestionnaire, et creer un modele le fait apparaitre dans le portefeuille de
// TOUTES les gestionnaires. Ce n'est pas une decision de caisse.
//
// L'APPLICATION, elle, reste au portefeuille : c'est un mouvement d'argent,
// date du jour, tire du budget depenses de celle qui l'applique.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Clock,
  CheckCircle,
  PauseCircle,
  Paperclip,
  Info,
  ShieldCheck} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import JustificatifInput from './JustificatifInput';
import EtatVide from './ui/EtatVide';
import Pagination from './ui/Pagination';
import PorteurNotification from './ui/PorteurNotification';
import VisionneuseJustificatif from './ui/VisionneuseJustificatif';
import { useNotification } from '../hooks/useNotification';
import { formaterFCFA } from '../utils/montants';
import { api } from '../services/api';

const PAR_PAGE = 10;

const FORMULAIRE_VIDE = {
  nom: '',
  montant: '',
  justificatif: '',
  justificatif_fichier: null,
  statut: 'actif'
};

const formaterDate = (valeur) =>
  valeur ? new Date(valeur).toLocaleDateString('fr-FR') : null;

/** Un interrupteur de réglage, avec son explication. */
const Interrupteur = ({ actif, enCours, onBasculer, titre, description }) => (
  <button
    type="button"
    onClick={onBasculer}
    disabled={enCours}
    className={`flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors ${actif ? 'border-[#246990]/30 bg-[#eef7fb]' : 'border-gray-200 bg-white hover:bg-gray-50'
      } disabled:opacity-60`}
  >
    <span
      className={`mt-0.5 flex h-6 w-11 flex-shrink-0 items-center rounded-full px-0.5 transition-colors ${actif ? 'bg-[#246990]' : 'bg-gray-300'
        }`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${actif ? 'translate-x-5' : 'translate-x-0'
          }`}
      >
        {enCours && <Loader2 size={11} className="animate-spin text-[#246990]" />}
      </span>
    </span>

    <span className="min-w-0">
      <span className="block text-sm font-semibold text-gray-900">{titre}</span>
      <span className="mt-0.5 block text-[11px] leading-snug text-gray-600">{description}</span>
    </span>
  </button>
);

const DepensesFixesSettingsPage = ({ onBackToDashboard }) => {
  const [modeles, setModeles] = useState([]);
  const [anneeActive, setAnneeActive] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [page, setPage] = useState(1);

  const { notification, notifier, masquer } = useNotification();

  // Un drapeau par chose distincte : le bouton d'enregistrement du dialogue
  // n'a rien a voir avec la ligne qu'on desactive, et deux lignes traitees
  // l'une apres l'autre ne doivent pas tourner ensemble.
  const [enregistrement, setEnregistrement] = useState(false);
  const [ligneEnCours, setLigneEnCours] = useState(null); // { id, action }

  const [dialogueCreation, setDialogueCreation] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);

  const [modeleEdite, setModeleEdite] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [pieceConsultee, setPieceConsultee] = useState(null);

  // Les réglages ont leur propre drapeau : enregistrer un interrupteur n'a
  // rien à voir avec l'enregistrement d'un modèle dans le dialogue.
  const [reglages, setReglages] = useState({
    justificatif_requis_creation: true,
    justificatif_requis_application: false
  });
  const [reglageEnCours, setReglageEnCours] = useState(null);

  const chargerModeles = useCallback(async (anneeId) => {
    try {
      const reponse = await api.depensesFixes.getAll(anneeId);
      if (reponse.success) {
        setModeles(Array.isArray(reponse.data) ? reponse.data : []);
      } else {
        notifier(reponse.message || 'Impossible de charger les dépenses fixes.', 'error');
      }
    } catch (erreur) {
      console.error('Erreur chargement des dépenses fixes :', erreur);
      notifier('Impossible de charger les dépenses fixes.', 'error');
    }
  }, [notifier]);

  useEffect(() => {
    const demarrer = async () => {
      try {
        const reponse = await api.anneesScolaires.getActive();
        const annee = reponse?.success ? reponse.data : null;
        setAnneeActive(annee);

        const [, reglagesReponse] = await Promise.all([
          chargerModeles(annee?.id),
          api.depensesFixes.getParametres(annee?.id)
        ]);
        if (reglagesReponse?.success && reglagesReponse.data) {
          setReglages({
            justificatif_requis_creation: Boolean(reglagesReponse.data.justificatif_requis_creation),
            justificatif_requis_application: Boolean(reglagesReponse.data.justificatif_requis_application)
          });
        }
      } finally {
        setChargement(false);
      }
    };
    demarrer();
  }, [chargerModeles]);

  /**
   * Bascule un interrupteur.
   *
   * L'état local suit tout de suite — un interrupteur qui attend le serveur
   * donne l'impression de ne pas répondre — mais il revient en arrière si
   * l'enregistrement échoue, plutôt que d'afficher un réglage que le serveur
   * n'applique pas.
   */
  const basculerReglage = async (cle) => {
    const avant = reglages;
    const apres = { ...reglages, [cle]: !reglages[cle] };

    setReglages(apres);
    setReglageEnCours(cle);
    try {
      const reponse = await api.depensesFixes.setParametres(anneeActive?.id, apres);
      if (reponse.success) {
        notifier('Réglage enregistré.', 'success');
      } else {
        setReglages(avant);
        notifier(reponse.message || 'Réglage non enregistré.', 'error');
      }
    } catch (erreur) {
      setReglages(avant);
      console.error('Erreur enregistrement réglage :', erreur);
      notifier('Réglage non enregistré.', 'error');
    } finally {
      setReglageEnCours(null);
    }
  };

  const rafraichir = useCallback(
    () => chargerModeles(anneeActive?.id),
    [chargerModeles, anneeActive]
  );

  const nombrePages = Math.max(1, Math.ceil(modeles.length / PAR_PAGE));
  const modelesPage = useMemo(
    () => modeles.slice((page - 1) * PAR_PAGE, page * PAR_PAGE),
    [modeles, page]
  );

  const totalMensuel = useMemo(
    () =>
      modeles
        .filter((m) => m.statut === 'actif')
        .reduce((somme, m) => somme + Number(m.montant || 0), 0),
    [modeles]
  );

  /* ------------------------------------------------------------------ */
  /* Création et modification                                            */
  /* ------------------------------------------------------------------ */

  const valider = (donnees, justificatifDejaPresent = false) => {
    if (!donnees.nom || !donnees.montant) {
      notifier('Renseignez le nom et le montant de la dépense fixe.', 'error');
      return false;
    }
    if (parseFloat(donnees.montant) <= 0) {
      notifier('Le montant doit être supérieur à 0 FCFA.', 'error');
      return false;
    }
    // L'exigence suit le réglage. Le serveur la revérifie : ce contrôle-ci
    // n'est qu'une politesse, pour ne pas envoyer une requête vouée au refus.
    if (reglages.justificatif_requis_creation && !donnees.justificatif_fichier && !justificatifDejaPresent) {
      notifier('Le justificatif est obligatoire pour une dépense fixe.', 'error');
      return false;
    }
    return true;
  };

  const creer = async () => {
    if (!valider(formulaire)) return;
    try {
      setEnregistrement(true);
      const reponse = await api.depensesFixes.create({
        nom: formulaire.nom,
        montant: parseFloat(formulaire.montant),
        justificatif: formulaire.justificatif,
        justificatif_fichier: formulaire.justificatif_fichier,
        statut: formulaire.statut,
        annee_scolaire_id: anneeActive?.id
      });
      if (reponse.success) {
        notifier('Dépense fixe créée.', 'success');
        setFormulaire(FORMULAIRE_VIDE);
        setDialogueCreation(false);
        await rafraichir();
      } else {
        notifier(reponse.message || 'Erreur lors de la création.', 'error');
      }
    } catch (erreur) {
      console.error('Erreur création dépense fixe :', erreur);
      notifier(erreur.message || 'Erreur lors de la création.', 'error');
    } finally {
      setEnregistrement(false);
    }
  };

  const modifier = async () => {
    if (!valider(modeleEdite, Boolean(modeleEdite?.justificatif_fichier_url))) return;
    try {
      setEnregistrement(true);
      const reponse = await api.depensesFixes.update(modeleEdite.id, {
        nom: modeleEdite.nom,
        montant: parseFloat(modeleEdite.montant),
        justificatif: modeleEdite.justificatif || '',
        justificatif_fichier: modeleEdite.justificatif_fichier || null
      });
      if (reponse.success) {
        notifier('Dépense fixe modifiée.', 'success');
        setModeleEdite(null);
        await rafraichir();
      } else {
        notifier(reponse.message || 'Erreur lors de la modification.', 'error');
      }
    } catch (erreur) {
      console.error('Erreur modification dépense fixe :', erreur);
      notifier(erreur.message || 'Erreur lors de la modification.', 'error');
    } finally {
      setEnregistrement(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Activation et suppression                                           */
  /* ------------------------------------------------------------------ */

  const executerSurLigne = async (modele, action, appel, messageSucces) => {
    try {
      setLigneEnCours({ id: modele.id, action });
      const reponse = await appel();
      if (reponse.success) {
        notifier(messageSucces, 'success');
        await rafraichir();
      } else {
        notifier(reponse.message || 'Opération impossible.', 'error');
      }
    } catch (erreur) {
      console.error(`Erreur ${action} dépense fixe :`, erreur);
      notifier(erreur.message || 'Opération impossible.', 'error');
    } finally {
      setLigneEnCours(null);
      setConfirmation(null);
    }
  };

  const demanderDesactivation = (modele) =>
    setConfirmation({
      type: 'warning',
      titre: 'Désactiver cette dépense fixe ?',
      description: `« ${modele.nom} » ne sera plus proposée à l'application dans les portefeuilles. Le modèle reste conservé et peut être réactivé à tout moment.`,
      libelle: 'Désactiver',
      action: () =>
        executerSurLigne(
          modele,
          'desactivation',
          () => api.depensesFixes.desactiver(modele.id),
          'Dépense fixe désactivée.'
        )
    });

  const demanderReactivation = (modele) =>
    setConfirmation({
      type: 'question',
      titre: 'Réactiver cette dépense fixe ?',
      description: `« ${modele.nom} » sera de nouveau proposée à l'application chaque mois, dans tous les portefeuilles.`,
      libelle: 'Réactiver',
      action: () =>
        executerSurLigne(
          modele,
          'reactivation',
          () => api.depensesFixes.update(modele.id, { statut: 'actif' }),
          'Dépense fixe réactivée.'
        )
    });

  const demanderSuppression = (modele) =>
    setConfirmation({
      type: 'error',
      titre: 'Supprimer cette dépense fixe ?',
      description: `« ${modele.nom} » disparaîtra définitivement. Les applications déjà passées en caisse ne sont pas touchées : elles restent dans l'historique des portefeuilles.`,
      libelle: 'Supprimer',
      action: () =>
        executerSurLigne(
          modele,
          'suppression',
          () => api.depensesFixes.delete(modele.id),
          'Dépense fixe supprimée.'
        )
    });

  /* ------------------------------------------------------------------ */

  if (chargement) {
    return (
      <div className="w-full">
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          titre="Paramétrage des dépenses fixes"
          sousTitre="Modèles de charges récurrentes applicables depuis les caisses"
        />
        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent="#246990" libelle="Chargement des dépenses fixes…" />
        </div>
      </div>
    );
  }

  const enCours = (modele, action) =>
    ligneEnCours?.id === modele.id && ligneEnCours?.action === action;
  const ligneOccupee = (modele) => ligneEnCours?.id === modele.id;

  return (
    <div className="w-full">
      {onBackToDashboard && (
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          icone={Clock}
          titre="Paramétrage des dépenses fixes"
          sousTitre="Modèles de charges récurrentes applicables depuis les caisses"
        />
      )}

      {/* Ce que fait un modèle, et ce qu'il ne fait pas.
          Sans cette mention, on croit créer une dépense — on ne fait que la
          rendre applicable. */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex items-start gap-3">
          <Info size={18} className="mt-0.5 flex-shrink-0 text-[#246990]" />
          <div>
            <p className="text-sm font-semibold text-[#246990]">
              Année scolaire {anneeActive?.nom || 'en cours'}
            </p>
            <p className="max-w-2xl text-[11px] leading-snug text-gray-600">
              Ces modèles valent pour toute l'école : ils apparaissent dans le portefeuille
              de chaque gestionnaire, qui les applique depuis sa caisse. Créer un modèle
              n'engage aucune dépense — c'est l'application, au portefeuille, qui sort
              l'argent.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Charge mensuelle active</p>
            <p className="text-lg font-bold tabular-nums text-[#246990]">
              {formaterFCFA(totalMensuel)}
            </p>
          </div>
          <Button
            size="sm"
            className="bg-[#246990] text-white hover:bg-[#1f5a7a]"
            onClick={() => {
              setFormulaire(FORMULAIRE_VIDE);
              setDialogueCreation(true);
            }}
          >
            <Plus size={16} className="mr-2" />
            Créer une dépense fixe
          </Button>
        </div>
      </div>

      {/* Les réglages gouvernent AUSSI les dépenses variables : ils ne sont
          pas propres aux modèles, d'où leur place à part, au-dessus. */}
      <div className="mb-6">
      <CarteReglages titre="Exigence de justificatif" accent="#246990">
        <div className="space-y-3">
          <Interrupteur
            actif={reglages.justificatif_requis_creation}
            enCours={reglageEnCours === 'justificatif_requis_creation'}
            onBasculer={() => basculerReglage('justificatif_requis_creation')}
            titre="À la création d'une dépense"
            description="Une dépense variable et un modèle de dépense fixe ne peuvent être créés sans pièce jointe."
          />

          <Interrupteur
            actif={reglages.justificatif_requis_application}
            enCours={reglageEnCours === 'justificatif_requis_application'}
            onBasculer={() => basculerReglage('justificatif_requis_application')}
            titre="À l'application d'une dépense fixe"
            description="La facture du mois est réclamée à chaque application. Le modèle porte le contrat ; il ne prouve pas le paiement du mois."
          />
        </div>
      </CarteReglages>
      </div>

      <CarteReglages
        titre="Modèles de l'année"
        accent="#319190"
        compteur={modeles.length ? `${modeles.length} modèle${modeles.length > 1 ? 's' : ''}` : null}
      >
        <div className="space-y-4">
          {modeles.length === 0 ? (
            <EtatVide
              icone={Clock}
              titre="Aucune dépense fixe"
              description="Le loyer, l'électricité, un abonnement : créez un modèle pour qu'il soit proposé chaque mois à l'application dans les portefeuilles."
              action={
                <Button
                  size="sm"
                  className="bg-[#246990] text-white hover:bg-[#1f5a7a]"
                  onClick={() => {
                    setFormulaire(FORMULAIRE_VIDE);
                    setDialogueCreation(true);
                  }}
                >
                  <Plus size={16} className="mr-2" />
                  Créer une dépense fixe
                </Button>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase text-gray-600">Dépense</th>
                      <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase text-gray-600">Montant mensuel</th>
                      <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase text-gray-600">Statut</th>
                      <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase text-gray-600">Ce mois</th>
                      <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase text-gray-600">Justificatif</th>
                      <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase text-gray-600">Créée par</th>
                      <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase text-gray-600">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {modelesPage.map((modele) => (
                      <tr key={modele.id} className="transition-colors hover:bg-[#eef7fb]/60">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{modele.nom}</p>
                          {modele.justificatif && (
                            <p className="mt-0.5 max-w-xs truncate text-[11px] text-gray-500">
                              {modele.justificatif}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900">
                          {formaterFCFA(modele.montant)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${modele.statut === 'actif'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                              }`}
                          >
                            {modele.statut === 'actif' ? <CheckCircle size={12} /> : <PauseCircle size={12} />}
                            {modele.statut === 'actif' ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        {/* L'application appartient au portefeuille, mais la
                            trace se lit ici : sans elle, on ne saurait pas si
                            le loyer est passé avant de toucher au modèle. */}
                        <td className="px-4 py-3 text-center">
                          {modele.appliquee_ce_mois ? (
                            <div className="text-[11px] leading-tight">
                              <p className="font-semibold text-emerald-700">
                                Appliquée le {formaterDate(modele.date_application)}
                              </p>
                              {modele.applique_par_nom && (
                                <p className="text-gray-500">
                                  {modele.applique_par_prenom} {modele.applique_par_nom}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400">Pas encore</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-center">
                          {modele.justificatif_fichier_url ? (
                            <button
                              type="button"
                              onClick={() => setPieceConsultee({
                                source: 'depense-fixe',
                                id: modele.id,
                                titre: modele.justificatif_fichier_nom || 'Justificatif',
                                sousTitre: modele.nom
                              })}
                              title={modele.justificatif_fichier_nom || 'Voir le justificatif'}
                              className="rounded-lg p-2 text-[#246990] transition-colors hover:bg-[#eef7fb]"
                            >
                              <Paperclip size={18} />
                            </button>
                          ) : (
                            <span className="text-[11px] text-gray-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-center text-[11px] text-gray-600">
                          {modele.created_by_prenom && modele.created_by_nom ? (
                            <>
                              <p>{modele.created_by_prenom} {modele.created_by_nom}</p>
                              <p className="text-gray-400">{formaterDate(modele.created_at)}</p>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              className="rounded-lg p-2 text-[#246990] transition-colors hover:bg-[#eef7fb] disabled:opacity-40"
                              title="Modifier"
                              disabled={ligneOccupee(modele)}
                              onClick={() => setModeleEdite({ ...modele, justificatif_fichier: null })}
                            >
                              <Edit size={16} />
                            </button>

                            {modele.statut === 'actif' ? (
                              <button
                                className="rounded-lg p-2 text-orange-600 transition-colors hover:bg-orange-50 disabled:opacity-40"
                                title="Désactiver"
                                disabled={ligneOccupee(modele)}
                                onClick={() => demanderDesactivation(modele)}
                              >
                                {enCours(modele, 'desactivation') ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <PauseCircle size={16} />
                                )}
                              </button>
                            ) : (
                              <button
                                className="rounded-lg p-2 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                                title="Réactiver"
                                disabled={ligneOccupee(modele)}
                                onClick={() => demanderReactivation(modele)}
                              >
                                {enCours(modele, 'reactivation') ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={16} />
                                )}
                              </button>
                            )}

                            <button
                              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
                              title="Supprimer"
                              disabled={ligneOccupee(modele)}
                              onClick={() => demanderSuppression(modele)}
                            >
                              {enCours(modele, 'suppression') ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                nombrePages={nombrePages}
                total={modeles.length}
                parPage={PAR_PAGE}
                onChangerPage={setPage}
                libelleElement="dépense fixe"
              />
            </>
          )}
        </div>
      </CarteReglages>

      {/* ------------------------------------------------------------------ */}
      {/* Création — sur les primitives Dialog, format large.                 */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogueCreation} onOpenChange={(ouvert) => !ouvert && setDialogueCreation(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Créer une dépense fixe</DialogTitle>
            <DialogDescription>
              Une charge récurrente de l'école. Elle sera proposée chaque mois à l'application
              dans les portefeuilles, sans être engagée automatiquement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Nom de la dépense <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Ex : loyer, électricité, internet…"
                  value={formulaire.nom}
                  onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Montant mensuel <span className="text-red-500">*</span>
                </label>
                <div className="flex overflow-hidden rounded-lg border border-gray-300">
                  <Input
                    type="number"
                    placeholder="0"
                    min="0"
                    step="1000"
                    value={formulaire.montant}
                    onChange={(e) => setFormulaire({ ...formulaire, montant: e.target.value })}
                    className="rounded-none border-0 focus:ring-0"
                  />
                  <span className="inline-flex items-center border-l border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-600">
                    FCFA
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Note interne</label>
              <textarea
                rows={3}
                placeholder="Contrat, bail, fournisseur, référence…"
                value={formulaire.justificatif}
                onChange={(e) => setFormulaire({ ...formulaire, justificatif: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
              />
            </div>

            <JustificatifInput
              required={reglages.justificatif_requis_creation}
              file={formulaire.justificatif_fichier}
              onFileChange={(fichier) => setFormulaire({ ...formulaire, justificatif_fichier: fichier })}
              title="Justificatif de dépense fixe"
              description="Contrat, facture type ou photo du document. Obligatoire à la création."
              accentColor="#246990"
            />

            <label className="flex cursor-pointer items-center gap-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formulaire.statut === 'actif'}
                onChange={(e) =>
                  setFormulaire({ ...formulaire, statut: e.target.checked ? 'actif' : 'inactif' })
                }
                className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
              />
              Proposer cette dépense à l'application chaque mois
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="h-9 min-w-[168px]"
              onClick={() => setDialogueCreation(false)}
              disabled={enregistrement}
            >
              Annuler
            </Button>
            <Button
              className="h-9 min-w-[168px] bg-[#246990] text-white hover:bg-[#1f5a7a]"
              onClick={creer}
              disabled={enregistrement}
            >
              {enregistrement ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Création…
                </>
              ) : (
                'Créer la dépense'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Modification                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={Boolean(modeleEdite)} onOpenChange={(ouvert) => !ouvert && setModeleEdite(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier la dépense fixe</DialogTitle>
            <DialogDescription>
              Le nouveau montant vaudra pour les prochaines applications. Celles déjà passées
              en caisse gardent le montant qu'elles portaient.
            </DialogDescription>
          </DialogHeader>

          {modeleEdite && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Nom de la dépense <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={modeleEdite.nom}
                    onChange={(e) => setModeleEdite({ ...modeleEdite, nom: e.target.value })}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Montant mensuel <span className="text-red-500">*</span>
                  </label>
                  <div className="flex overflow-hidden rounded-lg border border-gray-300">
                    <Input
                      type="number"
                      min="0"
                      step="1000"
                      value={modeleEdite.montant}
                      onChange={(e) => setModeleEdite({ ...modeleEdite, montant: e.target.value })}
                      className="rounded-none border-0 focus:ring-0"
                    />
                    <span className="inline-flex items-center border-l border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-600">
                      FCFA
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Note interne</label>
                <textarea
                  rows={3}
                  value={modeleEdite.justificatif || ''}
                  onChange={(e) => setModeleEdite({ ...modeleEdite, justificatif: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                />
              </div>

              <JustificatifInput
                required={reglages.justificatif_requis_creation}
                file={modeleEdite.justificatif_fichier || null}
                existingUrl={modeleEdite.justificatif_fichier_url || ''}
                existingSource="depense-fixe"
                existingId={modeleEdite.id}
                existingName={modeleEdite.justificatif_fichier_nom || ''}
                onFileChange={(fichier) => setModeleEdite({ ...modeleEdite, justificatif_fichier: fichier })}
                title="Justificatif de dépense fixe"
                description="Gardez le justificatif existant ou importez un nouveau document."
                accentColor="#246990"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              className="h-9 min-w-[168px]"
              onClick={() => setModeleEdite(null)}
              disabled={enregistrement}
            >
              Annuler
            </Button>
            <Button
              className="h-9 min-w-[168px] bg-[#246990] text-white hover:bg-[#1f5a7a]"
              onClick={modifier}
              disabled={enregistrement}
            >
              {enregistrement ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Enregistrement…
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={Boolean(confirmation)}
        onClose={() => setConfirmation(null)}
        onConfirm={() => confirmation?.action?.()}
        isLoading={Boolean(ligneEnCours)}
        type={confirmation?.type || 'info'}
        title={confirmation?.titre || ''}
        description={confirmation?.description || ''}
        confirmText={confirmation?.libelle || 'Confirmer'}
        cancelText="Annuler"
      />

      <VisionneuseJustificatif
        ouvert={Boolean(pieceConsultee)}
        onFermer={() => setPieceConsultee(null)}
        source={pieceConsultee?.source}
        id={pieceConsultee?.id}
        titre={pieceConsultee?.titre}
        sousTitre={pieceConsultee?.sousTitre}
      />

      <PorteurNotification notification={notification} onFermer={masquer} />
    </div>
  );
};

export default DepensesFixesSettingsPage;
